// Port of tools/attract-experiment/schedule.py. The analysis re-derives every
// session's loop from (seed, unit, UTC date, UTC hour), so this must stay
// bit-identical to the Python reference; shared/attract-schedule/vectors.json
// is the contract both sides are tested against.

export function fnv1a32(text: string): number {
  let hash = 0x811c9dc5;
  for (const byte of new TextEncoder().encode(text)) {
    hash = Math.imul(hash ^ byte, 0x01000193) >>> 0;
  }
  return hash;
}

export function mulberry32(seed: number): () => number {
  let state = seed >>> 0;
  return (): number => {
    state = (state + 0x6d2b79f5) >>> 0;
    let t = state;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

// Python's sorted() orders by code point; String#localeCompare and the default
// sort order by UTF-16 unit, which differs for astral characters.
function compareCodePoints(left: string, right: string): number {
  const a = Array.from(left, (char) => char.codePointAt(0) ?? 0);
  const b = Array.from(right, (char) => char.codePointAt(0) ?? 0);
  const length = Math.min(a.length, b.length);
  for (let index = 0; index < length; index++) {
    const difference = (a[index] ?? 0) - (b[index] ?? 0);
    if (difference !== 0) {
      return difference;
    }
  }
  return a.length - b.length;
}

export function blockOrder(
  seed: string,
  unit: string,
  utcDate: string,
  block: number,
  variants: readonly string[]
): string[] {
  const order = [...variants].sort(compareCodePoints);
  const random = mulberry32(fnv1a32(`${seed}|${unit}|${utcDate}|${block}`));
  for (let i = order.length - 1; i > 0; i--) {
    const j = Math.floor(random() * (i + 1));
    const swapped = order[i] as string;
    order[i] = order[j] as string;
    order[j] = swapped;
  }
  return order;
}

export function loopFor(
  seed: string,
  unit: string,
  utcDate: string,
  utcHour: number,
  variants: readonly string[]
): string {
  if (variants.length === 0) {
    throw new Error('at least one variant is required');
  }
  if (!Number.isInteger(utcHour) || utcHour < 0 || utcHour > 23) {
    throw new Error('utcHour must be 0..23');
  }
  const size = variants.length;
  const order = blockOrder(
    seed,
    unit,
    utcDate,
    Math.floor(utcHour / size),
    variants
  );
  return order[utcHour % size] as string;
}
